import { AlertTriangle, ShieldCheck } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { useAuthProviders } from '../../api/hooks'
import { Logo } from '../../components/layout/Logo'
import { Spinner } from '../../components/ui'
import { config } from '../../config'

/** Swedish text for the `error` codes the API redirects back with (routers/auth.py). */
const ERRORS: Record<string, string> = {
  wrong_domain: 'Kontot tillhör inte en organisation som har tillgång till plattformen.',
  wrong_tenant: 'Kontot tillhör inte Söderberg & Partners.',
  guest_not_allowed: 'Gästkonton kan inte logga in.',
  unverified_email: 'E-postadressen på kontot är inte verifierad.',
  no_email: 'Kontot saknar e-postadress.',
  no_role: 'Du har inte tilldelats någon roll i eventplattformen. Kontakta administratören.',
  inactive: 'Kontot är inaktiverat. Kontakta administratören.',
  provider_mismatch: 'Adressen är redan kopplad till en annan inloggningsmetod.',
  invalid_token: 'Inloggningen kunde inte verifieras. Prova igen.',
  state: 'Inloggningen tog för lång tid eller påbörjades i ett annat fönster. Prova igen.',
  provider_error: 'Inloggningstjänsten svarade inte. Prova igen om en stund.',
  cancelled: 'Inloggningen avbröts.',
}

const ICON: Record<string, React.ReactNode> = {
  microsoft: (
    <svg viewBox="0 0 21 21" className="size-4" aria-hidden><rect x="1" y="1" width="9" height="9" fill="#F25022" /><rect x="11" y="1" width="9" height="9" fill="#7FBA00" /><rect x="1" y="11" width="9" height="9" fill="#00A4EF" /><rect x="11" y="11" width="9" height="9" fill="#FFB900" /></svg>
  ),
  google: (
    <svg viewBox="0 0 48 48" className="size-4" aria-hidden><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6C12.3 13.6 17.7 9.5 24 9.5z" /><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4 7.1-10 7.1-17.5z" /><path fill="#FBBC05" d="M10.4 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.8-6A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.8-6z" /><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.9 2.3-8.4 2.3-6.3 0-11.7-4.1-13.6-9.9l-7.8 6C6.5 42.6 14.6 48 24 48z" /></svg>
  ),
}

export function LoginPage() {
  const [params] = useSearchParams()
  const error = params.get('error')
  const next = params.get('next') || '/'
  const providers = useAuthProviders()

  return (
    <div className="flex min-h-dvh flex-col bg-page">
      <div className="h-1.5 w-full bg-navy-900" />
      <main className="flex flex-1 items-center justify-center p-6">
        <div className="surface animate-fade-up w-full max-w-md p-8">
          <div className="mb-6 flex items-start"><Logo /></div>
          <h1 className="text-xl font-semibold text-ink-900">Logga in</h1>
          <p className="mt-1 text-sm text-ink-500">Eventplattform för Wealth Management. Använd ditt jobbkonto.</p>

          {error && (
            <div className="mt-5 flex gap-2.5 rounded-md border border-danger-500/30 bg-danger-500/5 p-3 text-sm text-ink-700">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-danger-500" />
              <span>{ERRORS[error] ?? 'Inloggningen misslyckades. Prova igen.'}</span>
            </div>
          )}

          <div className="mt-6 space-y-2.5">
            {providers.isLoading && <Spinner className="py-6" />}
            {providers.data?.providers.map((p, i) => (
              <a
                key={p.key}
                href={`${config.apiUrl}/api/auth/login/${p.key}?next=${encodeURIComponent(next)}`}
                style={{ animationDelay: `${60 + i * 50}ms` }}
                className="animate-fade-up flex h-11 items-center justify-center gap-3 rounded-md border border-navy-300 bg-white text-sm font-medium text-navy-800 transition-colors duration-[140ms] hover:border-navy-700 hover:bg-navy-50"
              >
                {ICON[p.key]}
                {p.label}
              </a>
            ))}
            {providers.data && providers.data.providers.length === 0 && (
              <p className="rounded-md border border-line-200 bg-page p-3 text-sm text-ink-500">
                Ingen inloggningsleverantör är konfigurerad. {providers.data.dev_switcher ? 'Lokalt utvecklingsläge: gå till startsidan så väljs en testanvändare automatiskt.' : 'Kontakta Solvigo.'}
              </p>
            )}
          </div>

          <p className="mt-8 flex items-start gap-2 text-xs leading-relaxed text-ink-500">
            <ShieldCheck className="mt-0.5 size-3.5 shrink-0 text-success-500" />
            Inga lösenord lagras här. Inloggningen sker hos din organisation och sessionen gäller i högst åtta timmar.
          </p>
        </div>
      </main>
      <footer className="px-6 pb-8 text-center text-xs text-ink-500">Söderberg &amp; Partners · Levererat av Solvigo</footer>
    </div>
  )
}
